import { useToast } from '@orbitalwatch/design';
import { useMemo } from 'react';
import { describeError, errorTitle } from './errorTitle';

/** Errors stay longer than the library's 3 s default: they carry the reason (a 403, a conflict). */
const ERROR_DURATION = 5000;

export interface Feedback {
    /** Success toast ("Page deleted."); title default "Done". */
    success: (text: string, title?: string) => void;
    /** Danger toast titled from the HTTP status ("Not allowed"), the API's message as text. */
    error: (error: unknown, title?: string) => void;
    /** Neutral info toast. */
    info: (text: string, title?: string) => void;
}

/**
 * Ark's replacement for `openPopup(<SuccessPopup>)` / `openPopup(<ErrorPopup>)` after a call: the library toasts
 * (`useToast`; `ToastProvider` is mounted by `App.tsx` and `src/test/render.tsx`). Confirmations that used
 * `WarningPopup` become a `ConfirmDrawer` or a `Dialog` with `danger`.
 */
export function useFeedback(): Feedback {
    const toast = useToast();
    return useMemo(() => ({
        success: (text, title) => toast.show({ tone: 'success', title: title ?? 'Done', text }),
        error: (error, title) => toast.show({ tone: 'danger', title: title ?? errorTitle(error), text: describeError(error), duration: ERROR_DURATION }),
        info: (text, title) => toast.show({ tone: 'info', title: title ?? 'Note', text }),
    }), [toast]);
}
